import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { QuoteApiService } from '../services/quote-api.service';
import { QuoteAttachmentApiService } from '../services/quote-attachment-api.service';
import {
  QuotePublicResponse,
  QuotePublicRejectRequest,
  QuoteRejectionReason,
  QuoteSignatureView,
  QUOTE_REJECTION_REASON_LABELS,
  quoteRejectionReasonLabel
} from '../models/quote.model';
import { QuoteAttachmentPublicResponse, formatAttachmentSize } from '../models/quote-attachment.model';
import { QuoteAttachmentIconComponent } from '../shared/quote-attachments/quote-attachment-icon.component';
import { QuoteDocumentComponent } from '../shared/quote-document/quote-document.component';
import { QuoteDocumentData, quoteDocumentFromPublic } from '../shared/quote-document/quote-document.model';
import { DatetimeService } from '../../../shared/services/datetime.service';

type SignTab = 'drawn' | 'typed' | 'upload';

/**
 * Portal público de cotización (sin auth): visualización + aceptación con firma
 * electrónica (RF-006/007/008) o rechazo con motivo. No usa servicios de sesión
 * ni ToastService (el contenedor de toasts vive en AppLayout) — todo el feedback
 * es inline (banners y mensajes en los modales).
 */
@Component({
  selector: 'app-public-quote',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, QuoteAttachmentIconComponent, QuoteDocumentComponent],
  templateUrl: './public-quote.component.html',
})
export class PublicQuoteComponent implements OnInit {
  token = '';
  loading = true;
  notFound = false;
  /** 410 Gone: el enlace apunta a una versión reemplazada por una más reciente. */
  replaced = false;
  replacedMessage = '';
  downloading = false;

  quote: QuotePublicResponse | null = null;

  // Documentos adjuntos públicos (carga silenciosa: error → sección oculta)
  attachments: QuoteAttachmentPublicResponse[] = [];
  private downloadingAttachmentIds = new Set<number>();

  // ---- Modal de firma ----
  showSignModal = false;
  signTab: SignTab = 'drawn';
  signForm: FormGroup;
  signing = false;
  signError = '';
  /** true justo después de firmar en esta sesión (para el mensaje de agradecimiento). */
  justSigned = false;

  // Canvas de firma manuscrita
  private canvas?: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null = null;
  private drawing = false;
  private lastX = 0;
  private lastY = 0;
  hasDrawing = false;

  // Firma subida (imagen)
  uploadedSignature: string | null = null;
  uploadError = '';

  // ---- Modal de rechazo ----
  showRejectModal = false;
  rejectStep: 'form' | 'confirm' = 'form';
  rejectForm: FormGroup;
  rejecting = false;
  rejectError = '';
  /** true justo después de rechazar en esta sesión. */
  justRejected = false;

  readonly rejectionReasons: { value: QuoteRejectionReason; label: string }[] =
    (Object.keys(QUOTE_REJECTION_REASON_LABELS) as QuoteRejectionReason[])
      .map(value => ({ value, label: QUOTE_REJECTION_REASON_LABELS[value] }));

  @ViewChild('signCanvas') set signCanvasRef(ref: ElementRef<HTMLCanvasElement> | undefined) {
    if (ref && ref.nativeElement !== this.canvas) {
      this.canvas = ref.nativeElement;
      this.initCanvas();
    } else if (!ref) {
      this.canvas = undefined;
      this.ctx = null;
    }
  }

  constructor(
    private route: ActivatedRoute,
    private quoteApi: QuoteApiService,
    private attachmentApi: QuoteAttachmentApiService,
    private datetime: DatetimeService,
    private fb: FormBuilder
  ) {
    this.signForm = this.fb.group({
      signatoryName: ['', [Validators.required, Validators.maxLength(200)]],
      signatoryEmail: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      consent: [false, [Validators.requiredTrue]]
    });

    this.rejectForm = this.fb.group({
      reason: ['', [Validators.required]],
      comment: ['', [Validators.maxLength(2000)]],
      rejectorName: ['', [Validators.maxLength(200)]],
      rejectorEmail: ['', [Validators.email, Validators.maxLength(255)]]
    });
  }

  ngOnInit(): void {
    this.token =
      this.route.snapshot.paramMap.get('token') ||
      this.route.snapshot.queryParamMap.get('token') ||
      '';

    if (!this.token) {
      this.notFound = true;
      this.loading = false;
      return;
    }

    this.cargar();
  }

  /** Carga (o recarga) los datos públicos de la cotización. */
  private cargar(): void {
    this.loading = true;
    this.quoteApi.getPublicQuote(this.token).subscribe({
      next: (quote) => {
        this.quote = quote;
        this.notFound = false;
        this.replaced = false;
        this.loading = false;
        this.cargarAdjuntos();
      },
      // 410 Gone: el enlace apunta a una versión "Reemplazada" — estado propio, tono
      // informativo. Cualquier otro error (404 por token inválido/revocado/rotado o
      // estado no compartible) se muestra de forma genérica, sin revelar si la
      // cotización existe.
      error: (err) => {
        if (err?.status === 410) {
          this.replaced = true;
          this.replacedMessage = err?.error?.message
            || 'Esta cotización fue reemplazada por una versión más reciente. Solicita el nuevo enlace a tu asesor comercial.';
        } else {
          this.notFound = true;
        }
        this.loading = false;
      },
    });
  }

  /** Carga silenciosa de los documentos públicos: cualquier error oculta la sección. */
  private cargarAdjuntos(): void {
    this.attachmentApi.getPublicAttachments(this.token).subscribe({
      next: (attachments) => {
        this.attachments = attachments || [];
      },
      error: () => {
        this.attachments = [];
      },
    });
  }

  // ---- Estado / CTAs ----

  get isAccepted(): boolean {
    return !!this.quote?.signed;
  }

  get isRejected(): boolean {
    return !this.isAccepted && !!this.quote?.rejectionReason;
  }

  get isExpired(): boolean {
    return this.quote?.statusName === 'Expirada';
  }

  /** "Aceptar y firmar" visible solo en Publicada/Vista, sin firma ni rechazo previos. */
  get canSign(): boolean {
    if (!this.quote || this.quote.signed || this.quote.rejectionReason) return false;
    const s = this.quote.statusName;
    return s === 'Publicada' || s === 'Vista';
  }

  /** "Rechazar" visible en Publicada/Vista/En Revisión, sin firma ni rechazo previos. */
  get canReject(): boolean {
    if (!this.quote || this.quote.signed || this.quote.rejectionReason) return false;
    const s = this.quote.statusName;
    return s === 'Publicada' || s === 'Vista' || s === 'En Revisión';
  }

  get rejectionReasonLabel(): string {
    return quoteRejectionReasonLabel(this.quote?.rejectionReason);
  }

  // ---- Descarga PDF ----

  descargarPdf(): void {
    if (!this.token || this.downloading) return;
    this.downloading = true;
    this.quoteApi.downloadPublicPdf(this.token).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.quote?.quoteNumber || 'cotizacion'}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.downloading = false;
      },
      error: () => {
        this.downloading = false;
      },
    });
  }

  // ---- Documentos adjuntos (portal público) ----

  isAttachmentDownloading(att: QuoteAttachmentPublicResponse): boolean {
    return this.downloadingAttachmentIds.has(att.id);
  }

  descargarAdjunto(att: QuoteAttachmentPublicResponse): void {
    if (!this.token || this.downloadingAttachmentIds.has(att.id)) return;
    this.downloadingAttachmentIds.add(att.id);
    this.attachmentApi.downloadPublicAttachment(this.token, att.id).subscribe({
      next: (blob) => {
        this.downloadingAttachmentIds.delete(att.id);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = att.fileName;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.downloadingAttachmentIds.delete(att.id);
      },
    });
  }

  attachmentSize(att: QuoteAttachmentPublicResponse): string {
    return formatAttachmentSize(att.sizeBytes);
  }

  // ---- Modal de firma ----

  abrirFirma(): void {
    this.signError = '';
    this.signTab = 'drawn';
    this.hasDrawing = false;
    this.uploadedSignature = null;
    this.uploadError = '';
    this.signForm.reset({ signatoryName: '', signatoryEmail: '', consent: false });
    this.showSignModal = true;
  }

  cerrarFirma(): void {
    if (this.signing) return;
    this.showSignModal = false;
  }

  setSignTab(tab: SignTab): void {
    if (this.signTab === tab) return;
    this.signTab = tab;
    this.signError = '';
    // El canvas se destruye al cambiar de pestaña: el trazo anterior se pierde.
    if (tab !== 'drawn') this.hasDrawing = false;
  }

  get signNameInvalid(): boolean {
    const c = this.signForm.get('signatoryName');
    return !!c && c.invalid && c.touched;
  }

  get signEmailInvalid(): boolean {
    const c = this.signForm.get('signatoryEmail');
    return !!c && c.invalid && c.touched;
  }

  get signConsentInvalid(): boolean {
    const c = this.signForm.get('consent');
    return !!c && c.invalid && c.touched;
  }

  /** Texto para la pre-visualización de la firma tipográfica. */
  get typedPreview(): string {
    const name = (this.signForm.get('signatoryName')?.value as string | null) || '';
    return name.trim() || 'Tu firma';
  }

  firmar(): void {
    this.signError = '';
    if (this.signForm.invalid) {
      this.signForm.markAllAsTouched();
      return;
    }

    let signatureData: string;
    let signatureType: 'drawn' | 'typed' | 'electronic';

    if (this.signTab === 'drawn') {
      if (!this.hasDrawing || !this.canvas) {
        this.signError = 'Dibuja tu firma en el recuadro antes de continuar.';
        return;
      }
      signatureData = this.canvas.toDataURL('image/png');
      signatureType = 'drawn';
    } else if (this.signTab === 'typed') {
      // Para 'typed' el backend renderiza el texto en itálica en el PDF.
      signatureData = ((this.signForm.get('signatoryName')?.value as string) || '').trim();
      signatureType = 'typed';
    } else {
      if (!this.uploadedSignature) {
        this.signError = 'Sube una imagen de tu firma antes de continuar.';
        return;
      }
      signatureData = this.uploadedSignature;
      signatureType = 'electronic';
    }

    const signatoryName = ((this.signForm.get('signatoryName')?.value as string) || '').trim();
    const signatoryEmail = ((this.signForm.get('signatoryEmail')?.value as string) || '').trim();

    this.signing = true;
    this.quoteApi.signPublicQuote(this.token, {
      signatoryName,
      signatoryEmail,
      signatureData,
      signatureType
    }).subscribe({
      next: () => {
        this.signing = false;
        this.showSignModal = false;
        this.justSigned = true;
        this.cargar();
      },
      error: (err) => {
        this.signing = false;
        // 409: la cotización cambió de estado (alguien ya actuó) — recargar y
        // mostrar el estado real. 400 (p. ej. "La cotización está vencida...")
        // muestra el message del backend inline, igual que el modal de rechazo.
        if (err?.status === 409) {
          this.showSignModal = false;
          this.cargar();
        } else {
          this.signError = err?.error?.message || 'No se pudo registrar la firma. Inténtalo de nuevo.';
        }
      }
    });
  }

  // ---- Canvas de firma manuscrita ----

  private initCanvas(): void {
    if (!this.canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const width = this.canvas.clientWidth || 400;
    const height = this.canvas.clientHeight || 176;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.ctx = this.canvas.getContext('2d');
    if (this.ctx) {
      this.ctx.scale(dpr, dpr);
      this.ctx.strokeStyle = '#1f2937';
      this.ctx.lineWidth = 2.5;
      this.ctx.lineCap = 'round';
      this.ctx.lineJoin = 'round';
    }
    this.hasDrawing = false;
  }

  private pointerPos(e: PointerEvent): { x: number; y: number } {
    const rect = this.canvas!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  onPointerDown(e: PointerEvent): void {
    if (!this.canvas || !this.ctx) return;
    e.preventDefault();
    this.canvas.setPointerCapture(e.pointerId);
    this.drawing = true;
    const { x, y } = this.pointerPos(e);
    this.lastX = x;
    this.lastY = y;
    // Punto inicial (cubre el caso de un toque sin arrastre)
    this.ctx.beginPath();
    this.ctx.moveTo(x, y);
    this.ctx.lineTo(x + 0.1, y + 0.1);
    this.ctx.stroke();
    this.hasDrawing = true;
  }

  onPointerMove(e: PointerEvent): void {
    if (!this.drawing || !this.ctx) return;
    e.preventDefault();
    const { x, y } = this.pointerPos(e);
    // Trazo suavizado: curva cuadrática hacia el punto medio
    const midX = (this.lastX + x) / 2;
    const midY = (this.lastY + y) / 2;
    this.ctx.beginPath();
    this.ctx.moveTo(this.lastX, this.lastY);
    this.ctx.quadraticCurveTo(this.lastX, this.lastY, midX, midY);
    this.ctx.lineTo(x, y);
    this.ctx.stroke();
    this.lastX = x;
    this.lastY = y;
  }

  onPointerUp(e: PointerEvent): void {
    if (!this.drawing) return;
    this.drawing = false;
    if (this.canvas?.hasPointerCapture(e.pointerId)) {
      this.canvas.releasePointerCapture(e.pointerId);
    }
  }

  limpiarFirma(): void {
    if (!this.canvas || !this.ctx) return;
    const dpr = window.devicePixelRatio || 1;
    this.ctx.clearRect(0, 0, this.canvas.width / dpr, this.canvas.height / dpr);
    this.hasDrawing = false;
  }

  // ---- Subir imagen de firma ----

  onSignatureFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    this.uploadError = '';
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      this.uploadError = 'Solo se permiten imágenes PNG o JPG.';
      input.value = '';
      return;
    }
    if (file.size > 1024 * 1024) {
      this.uploadError = 'La imagen no debe superar 1 MB.';
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.uploadedSignature = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  quitarImagenFirma(): void {
    this.uploadedSignature = null;
    this.uploadError = '';
  }

  // ---- Modal de rechazo ----

  abrirRechazo(): void {
    this.rejectError = '';
    this.rejectStep = 'form';
    this.rejectForm.reset({ reason: '', comment: '', rejectorName: '', rejectorEmail: '' });
    this.showRejectModal = true;
  }

  cerrarRechazo(): void {
    if (this.rejecting) return;
    this.showRejectModal = false;
  }

  get rejectReasonInvalid(): boolean {
    const c = this.rejectForm.get('reason');
    return !!c && c.invalid && c.touched;
  }

  get rejectEmailInvalid(): boolean {
    const c = this.rejectForm.get('rejectorEmail');
    return !!c && c.invalid && c.touched;
  }

  get rejectCommentLength(): number {
    return ((this.rejectForm.get('comment')?.value as string | null) || '').length;
  }

  /** Etiqueta del motivo seleccionado en el formulario (para el paso de confirmación). */
  get selectedReasonLabel(): string {
    return quoteRejectionReasonLabel((this.rejectForm.get('reason')?.value as string | null) || null);
  }

  continuarRechazo(): void {
    this.rejectError = '';
    if (this.rejectForm.invalid) {
      this.rejectForm.markAllAsTouched();
      return;
    }
    this.rejectStep = 'confirm';
  }

  volverARechazoForm(): void {
    this.rejectStep = 'form';
  }

  confirmarRechazo(): void {
    if (this.rejecting) return;
    this.rejectError = '';
    this.rejecting = true;

    const reason = this.rejectForm.get('reason')?.value as QuoteRejectionReason;
    const comment = ((this.rejectForm.get('comment')?.value as string | null) || '').trim();
    const rejectorName = ((this.rejectForm.get('rejectorName')?.value as string | null) || '').trim();
    const rejectorEmail = ((this.rejectForm.get('rejectorEmail')?.value as string | null) || '').trim();

    const req: QuotePublicRejectRequest = { reason };
    if (comment) req.comment = comment;
    if (rejectorName) req.rejectorName = rejectorName;
    if (rejectorEmail) req.rejectorEmail = rejectorEmail;

    this.quoteApi.rejectPublicQuote(this.token, req).subscribe({
      next: (resp) => {
        this.rejecting = false;
        this.showRejectModal = false;
        this.justRejected = true;
        // El backend INVALIDA el token al rechazar: un nuevo GET devolvería 404.
        // Confirmamos el rechazo con el body del POST + los datos del formulario,
        // sin re-consultar el token (que ya no existe).
        if (this.quote) {
          this.quote.statusName = resp?.status || 'Rechazada';
          this.quote.rejectionReason = reason;
          this.quote.rejectionComment = comment || null;
          this.quote.signed = false;
        }
      },
      error: (err) => {
        this.rejecting = false;
        if (err?.status === 409) {
          // Alguien ya actuó sobre la cotización: recargar y mostrar el estado real
          this.showRejectModal = false;
          this.cargar();
        } else {
          this.rejectStep = 'form';
          this.rejectError = err?.error?.details || err?.error?.message
            || 'No se pudo registrar el rechazo. Inténtalo de nuevo.';
        }
      }
    });
  }

  // ---- Formato / estilo ----

  get currencySymbol(): string {
    return this.quote?.currencySymbol || this.quote?.currencyCode || '';
  }

  formatFecha(iso?: string | null): string {
    return iso ? this.datetime.formatDate(iso) : '—';
  }

  formatFechaHora(iso?: string | null): string {
    return iso ? this.datetime.formatDateTimeShort(iso) : '—';
  }

  // Color de acento de la plantilla (si viene), con respaldo al brand del CRM.
  get accentColor(): string {
    return this.quote?.templatePrimaryColor || '#465fff';
  }

  // ---- Documento legal (formato cotizacion.html) ----

  /** Datos normalizados para el componente compartido <app-quote-document>. */
  get docData(): QuoteDocumentData | null {
    return this.quote ? quoteDocumentFromPublic(this.quote) : null;
  }

  /** Logo del proveedor: prioriza el del documento legal, cae al de la plantilla. */
  get logoUrl(): string | null {
    return this.quote?.providerLogoUrl || this.quote?.templateLogoUrl || null;
  }

  /** Nombre del proveedor con respaldo al vendedor. */
  get providerName(): string {
    return this.quote?.providerName || this.quote?.sellerName || '';
  }

  /** Nombre de la empresa cliente con respaldo a companyName. */
  get clientCompanyName(): string {
    return this.quote?.clientCompanyName || this.quote?.companyName || '';
  }

  /** Nombre de contacto del cliente con respaldo a contactName. */
  get clientContactName(): string {
    return this.quote?.clientContactName || this.quote?.contactName || '';
  }

  /** Etiqueta de moneda legible (ej. "Soles (S/.)"). */
  get currencyLabel(): string {
    return this.quote?.currencyLabel || this.quote?.currencyCode || this.currencySymbol || '';
  }

  /** Firmas del responsable (signerType=APPROVER), ordenadas por fecha desc. */
  get approverSignatures(): QuoteSignatureView[] {
    return (this.quote?.signatures || []).filter(s => s.signerType === 'APPROVER');
  }

  /** Firmas del cliente (signerType=CLIENT), ordenadas por fecha desc. */
  get clientSignatures(): QuoteSignatureView[] {
    return (this.quote?.signatures || []).filter(s => s.signerType === 'CLIENT');
  }

  /** Primera firma del responsable (la más reciente). */
  get approverSignature(): QuoteSignatureView | null {
    return this.approverSignatures[0] || null;
  }

  /** Primera firma del cliente (la más reciente). */
  get clientSignature(): QuoteSignatureView | null {
    return this.clientSignatures[0] || null;
  }

  /** true si la firma es una imagen (drawn/uploaded → data URL). */
  isImageSignature(sig: QuoteSignatureView | null): boolean {
    return !!sig?.signatureData && /^data:image\//i.test(sig.signatureData);
  }

  /** % del IGV formateado (taxRate viene como fracción, ej. 0.18 → 18). */
  get taxRatePercent(): number {
    return Math.round(((this.quote?.taxRate ?? 0) * 100) * 100) / 100;
  }
}
